import { XIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'sonner';

import { ReadOnlyField } from '@/components/atoms/ReadOnlyField/ReadOnlyField';
import {
  SidebarWrapper,
  SidebarWrapperContent,
  SidebarWrapperOverflowContainer,
  SidebarWrapperTitle,
} from '@/components/atoms/SidebarWrapper/SidebarWrapper';
import { MODAL_DELETE } from '@/components/organisms/Modals/ModalConductor';
import { Button } from '@/components/ui/button';
import useMembers from '@/hooks/useMembers';
import { useSidebar } from '@/hooks/useSidebar';
import { popModal, pushModal } from '@/redux/modals/actions';
import { getPermissions } from '@/redux/permissions/selectors';
import { trackEvent } from '@/segment/segment';
import { actions } from '@/shared/types/permissions';
import { EventName } from '@/shared/types/segment';
import { RootState } from '@/shared/types/state';
import { formatDate, timeFromNow } from '@/util/dateUtils';

import { apiKeyTypeLabelMapper } from './helper';
import { ResourceDeleteButton } from '../../components/ResourceList/ResourceDeleteButton';
import useCredentials from '../../hooks/useCredentials';
import { CredentialType } from '../../types';

const TYPE_DESCRIPTION_KEYS: Record<CredentialType, string> = {
  analytics: 'developer_tools.api_form.type.analytics_description',
  manage: 'developer_tools.api_form.type.manage_description',
  use: 'developer_tools.api_form.type.use_description',
};

/** "Oct 23, 2026 · 7 days ago", so the panel gives the exact date the tile leaves out. */
const formatWhen = (value: string) => `${formatDate(value, 'MMM d, yyyy')} · ${timeFromNow(value)}`;

/**
 * Read-only details of an API key: the tile's information with the exact dates and what the
 * key type allows. A key cannot be edited, only deleted.
 */
export const APIKeyPanel = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { meta, close } = useSidebar();
  const { credentials, listStatus, deleteCredential } = useCredentials();
  const { getMember } = useMembers();
  const canDelete = useSelector((state: RootState) =>
    getPermissions(state, 'credentials', actions.DELETE)
  );

  const credential = credentials.find((c) => c.credential_id === meta?.credentialId);
  const isLoading = listStatus === 'pending';
  const creator = credential?.created_by ? getMember(credential.created_by) : undefined;

  const handleDelete = () => {
    if (!credential) {
      return;
    }
    dispatch(
      pushModal(MODAL_DELETE, {
        title: t('developer_tools.delete_api_key.title'),
        subtitle: t('developer_tools.delete_api_key.description', { 0: credential.name }),
        onDelete: () => {
          trackEvent(EventName.ClickDeleteAPIKey);
          deleteCredential(credential.credential_id, {
            onSuccess: () => {
              toast.success(t('developer_tools.credentials.deleted', { 0: credential.name }));
              close();
            },
            onError: (error) => {
              toast.error(error.message);
            },
            onSettled: () => {
              dispatch(popModal());
            },
          });
        },
      })
    );
  };

  return (
    <SidebarWrapper>
      <SidebarWrapperTitle className="pr-4 pl-6">
        <span className="text-base font-medium text-foreground">
          {t('developer_tools.api_modal.api_key_label')}
        </span>
        <Button
          variant="ghost-neutral"
          size="icon-sm"
          onClick={close}
          aria-label={t('common.close')}
        >
          <XIcon size={16} />
        </Button>
      </SidebarWrapperTitle>

      <SidebarWrapperOverflowContainer className="pb-6">
        <SidebarWrapperContent className="px-1 pb-1">
          <ReadOnlyField label={t('common.name')} value={credential?.name} isLoading={isLoading} />

          <div className="space-y-1">
            <ReadOnlyField
              label={t('common.type')}
              value={credential ? apiKeyTypeLabelMapper(credential.type, t) : ''}
              isLoading={isLoading}
            />
            {credential && (
              <p className="text-xs leading-4 text-muted-foreground">
                {t(TYPE_DESCRIPTION_KEYS[credential.type])}
              </p>
            )}
          </div>

          <ReadOnlyField
            label={t('common.created_by')}
            value={creator?.name ?? '-'}
            isLoading={isLoading}
          />

          <ReadOnlyField
            label={t('common.created')}
            value={credential?.created ? formatWhen(credential.created) : '-'}
            isLoading={isLoading}
          />

          <ReadOnlyField
            label={t('common.last_used')}
            value={
              credential?.last_used_at
                ? formatWhen(credential.last_used_at)
                : t('common.not_used_yet')
            }
            isLoading={isLoading}
          />

          {credential && <ResourceDeleteButton onClick={handleDelete} disabled={!canDelete} />}
        </SidebarWrapperContent>
      </SidebarWrapperOverflowContainer>
    </SidebarWrapper>
  );
};
